import { useEffect, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Users, Vote, PenLine, Zap, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { Loading } from "@/components/common";

const NAVY = "#0f172a", CYAN = "#06b6d4", CORAL = "#f43f5e", SLATE = "#94a3b8";
const PIE_COLORS = [NAVY, CYAN, CORAL, "#f59e0b", "#8b5cf6", "#10b981", "#64748b", "#0ea5e9", "#e11d48", "#a3a3a3", "#14b8a6"];

// Stable references to avoid re-creating objects on every render
const AXIS_TICK = { fontSize: 11, fill: SLATE };
const TOOLTIP_STYLE = { borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 };
const LEGEND_STYLE = { fontSize: 12 };
const MARGIN_MAIN = { left: -18, right: 8, top: 5 };
const MARGIN_GROWTH = { left: -8, right: 8, top: 5 };

const fmtDate = (d) => {
  const [, m, day] = d.split("-");
  return `${day}/${m}`;
};

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setData(null);
    api.get("/admin/analytics", { params: { days } }).then((r) => setData(r.data)).catch(() => setData(false));
  }, [days]);

  if (data === null) return <Loading label="Cargando analítica…" />;
  if (data === false) return <p className="text-slate-400 py-8 text-center">No se pudo cargar la analítica.</p>;

  const t = data.totals;
  const cards = [
    { icon: Users, label: "Usuarios totales", value: t.users, sub: `+${t.new_users_period} en ${data.days} días`, color: "text-slate-900" },
    { icon: TrendingUp, label: "Participación (periodo)", value: t.participation_period, sub: "votos + firmas + relámpago", color: "text-cyan-600" },
    { icon: PenLine, label: "Firmas totales", value: t.signatures, sub: `${t.petitions} peticiones activas`, color: "text-rose-500" },
    { icon: Vote, label: "Votos en consultas", value: t.votes, sub: `${t.consultations} consultas`, color: "text-slate-900" },
  ];
  const dailyDates = data.daily.map((d) => ({ ...d, label: fmtDate(d.date) }));
  const growth = data.signature_growth.map((d) => ({ ...d, label: fmtDate(d.date) }));
  const roleData = Object.entries(data.roles).map(([name, value]) => ({ name, value }));

  return (
    <div data-testid="admin-analytics" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading font-bold text-xl text-slate-900">Analítica de participación</h2>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`analytics-range-${d}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium ${days === d ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
              {d} días
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={c.label} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`kpi-${i}`}>
            <c.icon className={`w-5 h-5 ${c.color} mb-2`} />
            <p className="font-heading font-black text-3xl text-slate-900">{(c.value ?? 0).toLocaleString("es-PR")}</p>
            <p className="text-sm text-slate-600 mt-1">{c.label}</p>
            <p className="text-xs text-slate-400 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Daily participation */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-heading font-bold text-slate-900 mb-4">Participación diaria</h3>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={dailyDates} margin={MARGIN_MAIN}>
            <defs>
              <linearGradient id="gPart" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CYAN} stopOpacity={0.4} />
                <stop offset="100%" stopColor={CYAN} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
            <XAxis dataKey="label" tick={AXIS_TICK} interval="preserveStartEnd" />
            <YAxis tick={AXIS_TICK} allowDecimals={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Area type="monotone" dataKey="participation" name="Participación" stroke={CYAN} strokeWidth={2.5} fill="url(#gPart)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Breakdown by type */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Por tipo (diario)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dailyDates} margin={MARGIN_MAIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="label" tick={AXIS_TICK} interval="preserveStartEnd" />
              <YAxis tick={AXIS_TICK} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="votes" name="Votos" stackId="a" fill={NAVY} />
              <Bar dataKey="flash" name="Relámpago" stackId="a" fill={CYAN} />
              <Bar dataKey="signatures" name="Firmas" stackId="a" fill={CORAL} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Signature growth */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Crecimiento acumulado de firmas</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={growth} margin={MARGIN_GROWTH}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="label" tick={AXIS_TICK} interval="preserveStartEnd" />
              <YAxis tick={AXIS_TICK} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Line type="monotone" dataKey="total" name="Firmas acumuladas" stroke={CORAL} strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top petitions */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2"><PenLine className="w-4 h-4 text-rose-500" /> Peticiones con más firmas</h3>
          <div className="space-y-3">
            {data.top_petitions.map((p) => {
              const pct = p.goal ? Math.min(100, Math.round((p.verified / p.goal) * 100)) : 0;
              return (
                <div key={p.id}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-slate-700 truncate mr-2">{p.title}</span><span className="font-semibold text-slate-900">{p.verified.toLocaleString("es-PR")}</span></div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-rose-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
            {data.top_petitions.length === 0 && <p className="text-sm text-slate-400">Sin datos.</p>}
          </div>
        </div>

        {/* Top consultations */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2"><Vote className="w-4 h-4 text-cyan-600" /> Consultas más votadas</h3>
          <div className="space-y-3">
            {data.top_consultations.map((c) => (
              <div key={c.id} className="flex justify-between text-sm">
                <span className="text-slate-700 truncate mr-2">{c.title}</span>
                <span className="font-semibold text-slate-900">{c.votes.toLocaleString("es-PR")}</span>
              </div>
            ))}
            {data.top_consultations.length === 0 && <p className="text-sm text-slate-400">Sin datos.</p>}
          </div>
        </div>

        {/* Roles distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-slate-700" /> Usuarios por rol</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={roleData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                {roleData.map((e, i) => <Cell key={e.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
            {roleData.map((r, i) => (
              <span key={r.name} className="inline-flex items-center gap-1 text-xs text-slate-500">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /> {r.name} ({r.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-400">Analítica de participación voluntaria en 2Voto. No representa una muestra científica de toda la población.</p>
    </div>
  );
}
